import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleExportPDF } from "../../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Preview = ({ tender, setStep, data, user }) => {
  const navigate = useNavigate();
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const pdfRef = useRef();
  const [exporting, setExporting] = useState(false);

  const groupedWorkItems = data?.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const handleNext = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const indexedData = data.map((item, index) => ({
        ...item,
        itemOrder: index,
      }));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/bid`,
        {
          vendorId: vendorId,
          tenderId: tender.tenderId,
          bidWorkItems: indexedData,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      navigate("/vendor/dashboard");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };
  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF, this may take a while.{" "}
          <CircularProgress color="#fff" />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(pdfRef, "Rebid_Submission");
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export PDF
        </Button>
      </div>
      {loading && (
        <div className="loading-div uploading-div">
          Submitting Bid... <CircularProgress color="#fff" />
        </div>
      )}
      <h2>Bid Submission Preview</h2>
      <Box className="dashboard-container" px={10} ref={pdfRef}>
        <Typography className="form-title">Work Item Details</Typography>
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Name</th>
              <th>Description</th>
              <th>Section</th>
              <th>Unit</th>
              <th>Quantity</th>
              <th>Rate (in &#8377;)</th>
              <th>Total (in &#8377;)</th>
            </tr>
          </thead>
          <tbody>
            {groupedWorkItems &&
              Object.entries(groupedWorkItems).map(
                ([section, items], sectionIndex) => {
                  const sectionTotal = items
                    .reduce(
                      (acc, item) =>
                        acc + parseFloat(item.quantity * item.rate || 0),
                      0
                    )
                    .toFixed(2);
                  return (
                    <React.Fragment key={sectionIndex}>
                      <tr
                        style={{
                          backgroundColor: "#d8e9f0",
                          fontWeight: "bold",
                        }}
                      >
                        <td
                          colSpan={6}
                          style={{ textAlign: "left", padding: "10px" }}
                        >
                          {section}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {sectionTotal &&
                            new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(sectionTotal)}
                        </td>
                      </tr>
                      {items.map((item, index) => (
                        <tr key={index} style={{ textAlign: "center" }}>
                          <td style={{ padding: "20px" }}>{item.itemName}</td>
                          <td>{item.description}</td>
                          <td>{item.section}</td>
                          <td>{item.unit}</td>
                          <td>{item.quantity}</td>
                          <td>
                            {new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(item.rate)}
                          </td>
                          <td>
                            {new Intl.NumberFormat("en-IN", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(
                              parseFloat(item.rate || 0) *
                                parseFloat(item.quantity || 0)
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                }
              )}
            <tr style={{ fontWeight: "bold", backgroundColor: "#d9edf7" }}>
              <td colSpan={6} style={{ textAlign: "right", padding: "8px" }}>
                Grand Total (in &#8377;):
              </td>
              <td style={{ textAlign: "center" }}>
                {new Intl.NumberFormat("en-IN", {
                  style: "decimal",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(
                  Object.values(groupedWorkItems)
                    .flat()
                    .reduce(
                      (acc, item) =>
                        acc + (parseFloat(item.quantity * item.rate) || 0),
                      0
                    )
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </Box>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpen(true)}>
          Submit
        </Button>
      </Box>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to submit these details?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              variant="outlined"
              onClick={() => setOpen(false)}
            >
              No
            </Button>
            <Button
              className="btn-contained"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Preview;
